/**
 * Push notifications: the alert catalogue, the admin settings and their validation, shared by the API
 * (sending, CRM and admin endpoints) and the web app (admin tab, client preferences).
 *
 * Every client notification has a category. A category is either *templated* (the API builds the text
 * from the admin's title / body templates: trade results, market, engagement, news) or written by the
 * code that sends it (deposits, KYC, compliance, margin), where the admin only decides whether it is
 * also pushed. *Mandatory* categories can't be muted by the client; *marketing* ones count towards the
 * marketing cap and are off for new clients unless the admin says otherwise. *Staff-managed* ones are
 * left out of the client's preferences: only the CRM can turn them off for a client.
 *
 * Alerts the client sets up themselves (price, move, portfolio: markets.ts, Account → Alerts) are a
 * separate system with its own channels; they are not categories here.
 */
export const PUSH_GROUPS = [
    { id: 'trading', label: 'Trading' },
    { id: 'risk', label: 'Risk' },
    { id: 'account', label: 'Account' },
    { id: 'market', label: 'Market' },
    { id: 'news', label: 'News & offers' },
];
export const PUSH_CATEGORY_KEYS = [
    'trade.tp_hit',
    'trade.sl_hit',
    'trade.orders',
    'trade.profit_milestone',
    'trade.daily_summary',
    'trade.weekly_summary',
    'risk.margin',
    'risk.stop_out',
    'account.funding',
    'account.verification',
    'account.message',
    'account.rewards',
    'security.new_login',
    'market.big_move',
    'market.event_risk',
    'news.broadcast',
    'engage.inactive',
    'engage.funded_not_traded',
    'engage.kyc_incomplete',
    'engage.deposit_abandoned',
];
const TRADE_PH = ['symbol', 'side', 'price', 'pnl', 'account'];
export const PUSH_CATEGORIES = [
    {
        key: 'trade.tp_hit',
        group: 'trading',
        label: 'Take profit hit',
        description: 'A position is closed by its take profit.',
        mandatory: false,
        marketing: false,
        templated: true,
        placeholders: TRADE_PH,
        thresholds: [],
        staleMinutes: 720,
    },
    {
        key: 'trade.sl_hit',
        group: 'trading',
        label: 'Stop loss hit',
        description: 'A position is closed by its stop loss.',
        mandatory: false,
        marketing: false,
        templated: true,
        placeholders: TRADE_PH,
        thresholds: [],
        staleMinutes: 720,
    },
    {
        key: 'trade.orders',
        group: 'trading',
        label: 'Orders filled',
        description: 'A limit or stop order is filled, or cancelled by the platform.',
        mandatory: false,
        marketing: false,
        templated: true,
        placeholders: ['symbol', 'side', 'price', 'type', 'account'],
        thresholds: [],
        staleMinutes: 720,
    },
    {
        key: 'trade.profit_milestone',
        group: 'trading',
        label: 'Profit milestones',
        description: 'An open position reaches a return on its margin, e.g. +10 %.',
        mandatory: false,
        marketing: false,
        templated: true,
        placeholders: ['symbol', 'side', 'pct', 'pnl', 'account'],
        thresholds: [
            { key: 'steps_pct', label: 'Return on margin steps (%)', type: 'list', min: 1, max: 10_000, hint: 'Each step fires once per position, e.g. 10, 25, 50, 100' },
            { key: 'min_pnl_usd', label: 'Minimum profit (USD)', type: 'number', min: 0, max: 1_000_000, hint: 'Skip positions whose profit is below this' },
        ],
        staleMinutes: 60,
    },
    {
        key: 'trade.daily_summary',
        group: 'trading',
        label: 'Daily summary',
        description: 'End of the day, for clients who closed trades that day.',
        mandatory: false,
        marketing: false,
        templated: true,
        placeholders: ['trades', 'pnl', 'date'],
        thresholds: [{ key: 'hour_utc', label: 'Send at (hour, UTC)', type: 'number', min: 0, max: 23 }],
        staleMinutes: 360,
    },
    {
        key: 'trade.weekly_summary',
        group: 'trading',
        label: 'Weekly summary',
        description: 'Monday morning: last week’s closed trades and result.',
        mandatory: false,
        marketing: false,
        templated: true,
        placeholders: ['trades', 'pnl', 'week'],
        thresholds: [{ key: 'hour_utc', label: 'Send on Monday at (hour, UTC)', type: 'number', min: 0, max: 23 }],
        staleMinutes: 720,
    },
    {
        key: 'risk.margin',
        group: 'risk',
        label: 'Margin warnings',
        description: 'The account’s margin level reaches the warning or critical level.',
        mandatory: true,
        marketing: false,
        templated: false,
        placeholders: [],
        thresholds: [],
        staleMinutes: 120,
    },
    {
        key: 'risk.stop_out',
        group: 'risk',
        label: 'Stop-out',
        description: 'A position is closed because the account reached the stop-out level.',
        mandatory: true,
        marketing: false,
        templated: false,
        placeholders: [],
        thresholds: [],
        staleMinutes: 720,
    },
    {
        key: 'account.funding',
        group: 'account',
        label: 'Deposits and withdrawals',
        description: 'A deposit is credited, a withdrawal or deposit request changes status.',
        mandatory: false,
        marketing: false,
        templated: false,
        placeholders: [],
        thresholds: [],
        staleMinutes: 1440,
        staffManaged: true,
    },
    {
        key: 'account.verification',
        group: 'account',
        label: 'Verification and compliance',
        description: 'KYC results, documents due or expired, source of funds.',
        mandatory: true,
        marketing: false,
        templated: false,
        placeholders: [],
        thresholds: [],
        staleMinutes: 1440,
        staffManaged: true,
    },
    {
        key: 'account.message',
        group: 'account',
        label: 'Messages from your account manager',
        description: 'Messages sent by staff.',
        mandatory: false,
        marketing: false,
        templated: false,
        placeholders: [],
        thresholds: [],
        staleMinutes: 1440,
        staffManaged: true,
    },
    {
        key: 'account.rewards',
        group: 'account',
        label: 'Rewards and yield',
        description: 'Rewards earned and yield paid out.',
        mandatory: false,
        marketing: false,
        templated: false,
        placeholders: [],
        thresholds: [],
        staleMinutes: 1440,
    },
    {
        key: 'security.new_login',
        group: 'account',
        label: 'New sign-in',
        description: 'Someone signs in to the account from a new device or browser.',
        mandatory: true,
        marketing: false,
        templated: true,
        placeholders: ['device', 'ip', 'time'],
        thresholds: [],
        staleMinutes: 1440,
    },
    {
        key: 'market.big_move',
        group: 'market',
        label: 'Big market moves',
        description: 'A market the client holds or has on a watchlist moves sharply in 24 hours.',
        mandatory: false,
        marketing: false,
        templated: true,
        placeholders: ['symbol', 'change', 'price', 'direction'],
        thresholds: [{ key: 'pct', label: '24 h move (%)', type: 'number', min: 1, max: 100 }],
        staleMinutes: 60,
    },
    {
        key: 'market.event_risk',
        group: 'market',
        label: 'High-impact events for markets held',
        description: 'A high-impact economic release (economic calendar) is coming up for a market the client holds. On for every client; they can turn it off.',
        mandatory: false,
        marketing: false,
        templated: true,
        placeholders: ['event', 'symbol', 'minutes', 'side', 'guide'],
        thresholds: [{ key: 'minutes', label: 'Minutes before the release', type: 'number', min: 5, max: 240 }],
        staleMinutes: 30,
    },
    {
        key: 'news.broadcast',
        group: 'news',
        label: 'News and offers',
        description: 'Market news and offers sent by the team.',
        mandatory: false,
        marketing: true,
        templated: false,
        placeholders: [],
        thresholds: [],
        staleMinutes: 1440,
    },
    {
        key: 'engage.inactive',
        group: 'news',
        label: 'We miss you',
        description: 'The client hasn’t signed in for a number of days.',
        mandatory: false,
        marketing: true,
        templated: true,
        placeholders: ['first_name', 'days'],
        thresholds: [{ key: 'days', label: 'Days without signing in', type: 'list', min: 1, max: 365, hint: 'e.g. 3, 7, 14' }],
        staleMinutes: 1440,
        staffManaged: true,
    },
    {
        key: 'engage.funded_not_traded',
        group: 'news',
        label: 'Ready to trade',
        description: 'The client deposited but hasn’t placed a trade yet.',
        mandatory: false,
        marketing: true,
        templated: true,
        placeholders: ['first_name'],
        thresholds: [{ key: 'hours', label: 'Hours after the first deposit', type: 'number', min: 1, max: 720 }],
        staleMinutes: 1440,
    },
    {
        key: 'engage.kyc_incomplete',
        group: 'news',
        label: 'Finish verification',
        description: 'The client registered but hasn’t completed verification.',
        mandatory: false,
        marketing: true,
        templated: true,
        placeholders: ['first_name', 'days'],
        thresholds: [{ key: 'days', label: 'Days after registering', type: 'number', min: 1, max: 365 }],
        staleMinutes: 1440,
        staffManaged: true,
    },
    {
        key: 'engage.deposit_abandoned',
        group: 'news',
        label: 'Unfinished deposit',
        description: 'A crypto deposit was started but never paid.',
        mandatory: false,
        marketing: true,
        templated: true,
        placeholders: ['first_name', 'amount'],
        thresholds: [{ key: 'hours', label: 'Hours after it was started', type: 'number', min: 1, max: 168 }],
        staleMinutes: 1440,
    },
];
export const pushCategory = (key) => PUSH_CATEGORIES.find((c) => c.key === key);
export const isPushCategory = (key) => typeof key === 'string' && PUSH_CATEGORY_KEYS.includes(key);
const t = (title, body, link, extra = {}) => ({
    enabled: true,
    push: true,
    defaultOn: true,
    audience: 'both',
    title,
    body,
    link,
    cooldownMin: 0,
    thresholds: {},
    ...extra,
});
export const DEFAULT_PUSH_SETTINGS = {
    enabled: true,
    quietHours: { enabled: true, start: '22:00', end: '08:00' },
    dailyCap: 8,
    marketingDailyCap: 1,
    showAmounts: true,
    categories: {
        'trade.tp_hit': t('Take profit hit: {symbol}', 'Your {side} {symbol} position closed at {price}, {pnl}.', '/portfolio'),
        'trade.sl_hit': t('Stop loss hit: {symbol}', 'Your {side} {symbol} position closed at {price}, {pnl}.', '/portfolio'),
        'trade.orders': t('Order filled: {symbol}', 'Your {type} {side} order on {symbol} filled at {price}.', '/portfolio'),
        'trade.profit_milestone': t('{symbol} is up {pct}', 'Your {side} {symbol} position is up {pct} on margin ({pnl}).', '/portfolio', {
            audience: 'live',
            cooldownMin: 30,
            thresholds: { steps_pct: '10, 25, 50, 100', min_pnl_usd: '20' },
        }),
        'trade.daily_summary': t('Your day: {pnl}', '{trades} trades closed today, {pnl} realised.', '/portfolio', {
            audience: 'live',
            defaultOn: false,
            thresholds: { hour_utc: '21' },
        }),
        'trade.weekly_summary': t('Your week: {pnl}', '{trades} trades closed last week, {pnl} realised.', '/portfolio', {
            audience: 'live',
            thresholds: { hour_utc: '8' },
        }),
        'risk.margin': t('', '', ''),
        'risk.stop_out': t('', '', ''),
        'account.funding': t('', '', ''),
        'account.verification': t('', '', ''),
        'account.message': t('', '', ''),
        'account.rewards': t('', '', ''),
        'security.new_login': t('New sign-in to your account', 'Signed in on {device} at {time} UTC. Not you? Change your password now.', '/account/security'),
        'market.big_move': t('{symbol} is {direction} {change} today', '{symbol} is at {price}, {direction} {change} in 24 hours.', '/trade/{symbol}', {
            cooldownMin: 360,
            thresholds: { pct: '5' },
        }),
        'market.event_risk': t('{event} in {minutes} min', 'You hold {symbol} ({side}). Around the release prices can jump, spreads can widen and a stop loss can close at a worse price.', '/markets/calendar/{guide}', { audience: 'both', defaultOn: true, thresholds: { minutes: '30' } }),
        'news.broadcast': t('', '', '', { defaultOn: true }),
        'engage.inactive': t('Markets are moving, {first_name}', 'It’s been {days} days. See what’s moving today.', '/markets', {
            audience: 'both',
            thresholds: { days: '3, 7, 14' },
        }),
        'engage.funded_not_traded': t('Your account is ready, {first_name}', 'Your deposit is in. Place your first trade in a few taps.', '/markets', {
            audience: 'live',
            thresholds: { hours: '48' },
        }),
        'engage.kyc_incomplete': t('Finish verifying your account', 'It only takes a few minutes, then deposits and withdrawals are unlocked.', '/account/kyc', {
            thresholds: { days: '2' },
        }),
        'engage.deposit_abandoned': t('Your deposit is waiting', 'Your {amount} deposit wasn’t completed. Pick up where you left off.', '/account/deposit', {
            audience: 'live',
            thresholds: { hours: '2' },
        }),
    },
};
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const LINK_RE = /^(\/[^/\\]|https:\/\/)/;
/** Parse a comma-separated threshold list into sorted unique numbers. */
export function thresholdList(v) {
    return [...new Set((v ?? '').split(',').map((s) => Number(s.trim())).filter((x) => Number.isFinite(x) && x > 0))].sort((a, b) => a - b);
}
export const thresholdNum = (v, def) => {
    const n = Number(v);
    return v !== undefined && v.trim() !== '' && Number.isFinite(n) ? n : def;
};
/** Validate settings from the admin tab or the CRM. Missing categories take their defaults. */
export function parsePushSettings(input) {
    const b = input;
    if (!b || typeof b !== 'object')
        return { ok: false, error: 'settings must be an object' };
    if (typeof b.enabled !== 'boolean')
        return { ok: false, error: 'enabled must be true or false' };
    if (typeof b.showAmounts !== 'boolean')
        return { ok: false, error: 'showAmounts must be true or false' };
    const qh = b.quietHours;
    if (!qh || typeof qh.enabled !== 'boolean' || !HHMM.test(qh.start) || !HHMM.test(qh.end)) {
        return { ok: false, error: 'quietHours needs enabled and start / end as HH:MM' };
    }
    for (const k of ['dailyCap', 'marketingDailyCap']) {
        if (!Number.isInteger(b[k]) || b[k] < 0 || b[k] > 100)
            return { ok: false, error: `${k} must be a whole number 0–100` };
    }
    const categories = {};
    const src = b.categories && typeof b.categories === 'object' ? b.categories : {};
    for (const k of Object.keys(src))
        if (!isPushCategory(k))
            return { ok: false, error: `Unknown category ${k}` };
    for (const def of PUSH_CATEGORIES) {
        const d = DEFAULT_PUSH_SETTINGS.categories[def.key];
        const c = src[def.key] ?? d;
        const name = `categories.${def.key}`;
        for (const f of ['enabled', 'push', 'defaultOn'])
            if (typeof c[f] !== 'boolean')
                return { ok: false, error: `${name}.${f} must be true or false` };
        if (!['live', 'demo', 'both'].includes(c.audience))
            return { ok: false, error: `${name}.audience must be live, demo or both` };
        if (!Number.isInteger(c.cooldownMin) || c.cooldownMin < 0 || c.cooldownMin > 43_200)
            return { ok: false, error: `${name}.cooldownMin must be 0–43200 minutes` };
        let title = d.title;
        let body = d.body;
        let link = d.link;
        if (def.templated) {
            title = typeof c.title === 'string' ? c.title.trim() : '';
            body = typeof c.body === 'string' ? c.body.trim() : '';
            link = typeof c.link === 'string' ? c.link.trim() : '';
            if (!title || title.length > 120)
                return { ok: false, error: `${def.label}: title is required (≤120 characters)` };
            if (body.length > 300)
                return { ok: false, error: `${def.label}: text must be at most 300 characters` };
            if (link && (link.length > 300 || !LINK_RE.test(link)))
                return { ok: false, error: `${def.label}: link must be an app path like /portfolio or an https:// URL` };
            const unknown = [...`${title} ${body} ${link}`.matchAll(/\{(\w+)\}/g)].map((m) => m[1]).find((p) => !def.placeholders.includes(p));
            if (unknown)
                return { ok: false, error: `${def.label}: unknown placeholder {${unknown}}; use ${def.placeholders.map((p) => `{${p}}`).join(', ')}` };
        }
        const thresholds = {};
        for (const th of def.thresholds) {
            const raw = String(c.thresholds?.[th.key] ?? d.thresholds[th.key] ?? '').trim();
            if (th.type === 'list') {
                const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
                if (!parts.length || parts.length > 10 || parts.some((p) => !/^\d+(\.\d+)?$/.test(p) || Number(p) < th.min || Number(p) > th.max)) {
                    return { ok: false, error: `${def.label}: ${th.label} must be 1–10 numbers between ${th.min} and ${th.max}, separated by commas` };
                }
                thresholds[th.key] = thresholdList(raw).join(', ');
            }
            else {
                if (!/^\d+(\.\d+)?$/.test(raw) || Number(raw) < th.min || Number(raw) > th.max)
                    return { ok: false, error: `${def.label}: ${th.label} must be between ${th.min} and ${th.max}` };
                thresholds[th.key] = String(Number(raw));
            }
        }
        categories[def.key] = { enabled: c.enabled, push: c.push, defaultOn: c.defaultOn, audience: c.audience, title, body, link, cooldownMin: c.cooldownMin, thresholds };
    }
    return {
        ok: true,
        settings: {
            enabled: b.enabled,
            quietHours: { enabled: qh.enabled, start: qh.start, end: qh.end },
            dailyCap: b.dailyCap,
            marketingDailyCap: b.marketingDailyCap,
            showAmounts: b.showAmounts,
            categories,
        },
    };
}
/** Merge stored settings over the defaults (new categories added by a release take their defaults). */
export function mergePushSettings(stored) {
    const s = (stored ?? {});
    const categories = { ...DEFAULT_PUSH_SETTINGS.categories };
    for (const def of PUSH_CATEGORIES) {
        const c = s.categories?.[def.key];
        if (c)
            categories[def.key] = { ...categories[def.key], ...c, thresholds: { ...categories[def.key].thresholds, ...(c.thresholds ?? {}) } };
    }
    return {
        ...DEFAULT_PUSH_SETTINGS,
        ...s,
        quietHours: { ...DEFAULT_PUSH_SETTINGS.quietHours, ...(s.quietHours ?? {}) },
        categories,
    };
}
/** Fill `{name}` placeholders; unknown or missing ones become empty. */
/**
 * Emoji, and the joiners and variation selectors that build them. Notifications carry their own icons
 * (the bell, toasts and pushes), so text never brings its own. Plain symbols such as © or → stay.
 */
const EMOJI = /(?:\p{Extended_Pictographic}\uFE0F|\p{Emoji_Presentation}|\p{Emoji_Modifier}|[\u200D\uFE0F\u20E3])/gu;
/** The text without emoji, spacing tidied where one was removed. */
export const stripEmoji = (s) => s
    .replace(EMOJI, '')
    .replace(/[^\S\n]{2,}/g, ' ')
    .replace(/\s+([,.!?:])/g, '$1')
    .trim();
export function renderPushTemplate(tpl, vars) {
    return stripEmoji(tpl)
        .replace(/\{(\w+)\}/g, (_m, k) => {
        const v = vars[k];
        return v === null || v === undefined ? '' : String(v);
    })
        .replace(/\s+([,.!?])/g, '$1')
        .replace(/,\s*,/g, ',')
        .replace(/\s{2,}/g, ' ')
        .trim()
        .replace(/[\s,]+$/, '');
}
/** Minutes since midnight for "HH:MM". */
export const hhmmMinutes = (s) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
/**
 * Whether `now` falls in the quiet window in time zone `tz` and, if so, the instant it ends.
 * A window may cross midnight (22:00–08:00). Unknown time zones count as UTC.
 */
export function quietUntil(now, tz, start, end) {
    const s = hhmmMinutes(start);
    const e = hhmmMinutes(end);
    if (s === e)
        return null;
    let local;
    try {
        const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
        local = Number(parts.find((p) => p.type === 'hour').value) * 60 + Number(parts.find((p) => p.type === 'minute').value);
    }
    catch {
        local = now.getUTCHours() * 60 + now.getUTCMinutes();
    }
    const inside = s < e ? local >= s && local < e : local >= s || local < e;
    if (!inside)
        return null;
    const wait = (e - local + 1440) % 1440;
    return new Date(Math.floor(now.getTime() / 60_000) * 60_000 + wait * 60_000);
}
export const CAMPAIGN_AUDIENCES = [
    { id: 'all', label: 'All clients' },
    { id: 'funded', label: 'Funded live accounts' },
    { id: 'not_funded', label: 'Never deposited' },
    { id: 'demo_only', label: 'Demo traders (never deposited, traded on demo)' },
    { id: 'inactive', label: 'Inactive for N days' },
    { id: 'funded_not_traded', label: 'Deposited, never traded' },
    { id: 'kyc_incomplete', label: 'Verification not completed' },
    { id: 'holding', label: 'Holding an instrument' },
    { id: 'users', label: 'Specific clients (ids or emails)' },
];
/** Validate a new campaign. `scheduledAt` null = send now. */
export function parseCampaign(input) {
    const b = input;
    const title = typeof b?.title === 'string' ? b.title.trim() : '';
    const body = typeof b?.body === 'string' ? b.body.trim() : '';
    if (!title || title.length > 120)
        return { ok: false, error: 'title is required (≤120 characters)' };
    if (!body || body.length > 300)
        return { ok: false, error: 'body is required (≤300 characters)' };
    const link = typeof b.link === 'string' && b.link.trim() ? b.link.trim() : null;
    if (link && (link.length > 300 || !LINK_RE.test(link)))
        return { ok: false, error: 'link must be an app path like /markets or an https:// URL' };
    const s = b.segment;
    if (!s || !CAMPAIGN_AUDIENCES.some((a) => a.id === s.audience))
        return { ok: false, error: `segment.audience must be ${CAMPAIGN_AUDIENCES.map((a) => a.id).join(', ')}` };
    const segment = { audience: s.audience };
    if (s.audience === 'inactive') {
        if (!Number.isInteger(s.days) || s.days < 1 || s.days > 3650)
            return { ok: false, error: 'segment.days must be 1–3650' };
        segment.days = s.days;
    }
    if (s.audience === 'holding') {
        if (typeof s.symbol !== 'string' || !/^[A-Za-z0-9._:-]{1,40}$/.test(s.symbol))
            return { ok: false, error: 'segment.symbol is required' };
        segment.symbol = s.symbol.toUpperCase();
    }
    if (s.audience === 'users') {
        const users = Array.isArray(s.users) ? s.users.map((u) => String(u).trim()).filter(Boolean) : [];
        if (!users.length || users.length > 5000)
            return { ok: false, error: 'segment.users must list 1–5000 user ids or emails' };
        segment.users = [...new Set(users)];
    }
    if (s.countries !== undefined && s.countries !== null) {
        const cs = Array.isArray(s.countries) ? s.countries.map((c) => String(c).trim().toUpperCase()).filter(Boolean) : null;
        if (!cs || cs.length > 250 || cs.some((c) => !/^[A-Z]{2}$/.test(c)))
            return { ok: false, error: 'segment.countries must be two-letter country codes' };
        if (cs.length)
            segment.countries = cs;
    }
    let scheduledAt = null;
    if (b.scheduledAt !== undefined && b.scheduledAt !== null && b.scheduledAt !== '') {
        const d = new Date(String(b.scheduledAt));
        if (Number.isNaN(d.getTime()))
            return { ok: false, error: 'scheduledAt must be an ISO date-time' };
        if (d.getTime() > Date.now() + 90 * 86_400_000)
            return { ok: false, error: 'scheduledAt must be within 90 days' };
        if (d.getTime() > Date.now() + 60_000)
            scheduledAt = d;
    }
    return { ok: true, title, body, link, segment, scheduledAt };
}
//# sourceMappingURL=push.js.map